import { Check, Plus, Trash2 } from 'lucide-react'
import { Modal } from '@shared/ui/Modal'
import { ENV } from '@shared/config/env'
import type {
  PaymentMethodResponse,
  PaymentProvider,
} from '@features/payments'
import tributeLogo from '@/assets/images/payments/tribute.png'
import robokassaLogo from '@/assets/images/payments/robokassa.png'
import visaLogo from '@/assets/images/payments/visa.png'
import mirLogo from '@/assets/images/payments/mir.png'
import styles from './ManagePaymentMethodsModal.module.css'

const PROVIDER_LABEL: Record<PaymentProvider, string> = {
  robokassa: 'Робокасса',
  tribute: 'Tribute',
}

const PROVIDER_LOGO: Record<PaymentProvider, string> = {
  robokassa: robokassaLogo,
  tribute: tributeLogo,
}

const getCardBrandLogo = (brand: string | null): string | null => {
  if (!brand) return null
  const normalized = brand.toLowerCase()
  if (normalized.includes('visa')) return visaLogo
  if (normalized.includes('mir') || normalized.includes('мир')) return mirLogo
  return null
}

const getMaskedTail = (masked: string): string => {
  const digits = masked.replace(/\D/g, '')
  const tail = digits.slice(-4)
  return tail ? `**** **** **** ${tail}` : masked
}

interface ManagePaymentMethodsModalProps {
  methods: PaymentMethodResponse[]
  isLoading: boolean
  /** Идёт привязка новой карты — блокируем кнопку */
  isAddingCard: boolean
  /** Идёт смена основной карты — блокируем радио */
  isSettingMain: boolean
  /** Идёт удаление — блокируем кнопки удаления */
  isDeleting: boolean
  onSetMain: (method: PaymentMethodResponse) => void
  onDeleteRequest: (method: PaymentMethodResponse) => void
  onAddCard: () => void
  onClose: () => void
}

/**
 * Модалка управления способами оплаты: список привязанных карт,
 * выбор основной (радио), удаление и добавление новой.
 * Если карта одна — она всегда основная, выбор не нужен.
 */
export const ManagePaymentMethodsModal = ({
  methods,
  isLoading,
  isAddingCard,
  isSettingMain,
  isDeleting,
  onSetMain,
  onDeleteRequest,
  onAddCard,
  onClose,
}: ManagePaymentMethodsModalProps) => {
  const variant = ENV.IS_TG ? 'bottomSheet' : 'center'
  const singleCard = methods.length === 1

  return (
    <Modal onClose={onClose} variant={variant} showClose>
      <div className={styles.content}>
        <h3 className={styles.title}>Способы оплаты</h3>

        <div className={styles.list}>
          {isLoading && methods.length === 0 && (
            <p className={styles.empty}>Загружаем способы оплаты…</p>
          )}

          {methods.map(method => {
            const brandLogo = getCardBrandLogo(method.card_brand)
            // Одна карта — она по определению основная: радио не показываем.
            const isSelected = singleCard || method.is_main
            return (
              <div key={method.payment_method_id} className={styles.card}>
                <div className={styles.cardMain}>
                  <img
                    src={PROVIDER_LOGO[method.provider]}
                    alt={PROVIDER_LABEL[method.provider]}
                    className={styles.providerLogo}
                  />
                  <div className={styles.cardInfo}>
                    <span className={styles.providerName}>
                      {PROVIDER_LABEL[method.provider]}
                    </span>
                    <span className={styles.maskedNumber}>
                      {getMaskedTail(method.masked_card_number)}
                    </span>
                  </div>
                  {brandLogo && (
                    <img
                      src={brandLogo}
                      alt={method.card_brand ?? ''}
                      className={styles.brandLogo}
                    />
                  )}
                </div>

                <div className={styles.cardActions}>
                  <button
                    type="button"
                    className={styles.iconButton}
                    onClick={() => onDeleteRequest(method)}
                    disabled={isDeleting}
                    aria-label="Удалить способ оплаты"
                  >
                    <Trash2 size={20} strokeWidth={1.6} />
                  </button>
                  {!singleCard && (
                    <button
                      type="button"
                      className={`${styles.radio} ${isSelected ? styles.radioActive : ''}`}
                      onClick={() => onSetMain(method)}
                      disabled={method.is_main || isSettingMain}
                      aria-label={
                        method.is_main
                          ? 'Способ оплаты по умолчанию'
                          : 'Сделать способом по умолчанию'
                      }
                    >
                      {isSelected && <Check size={14} strokeWidth={3} />}
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        <button
          type="button"
          className={styles.addCard}
          onClick={onAddCard}
          disabled={isAddingCard}
        >
          <span className={styles.addPlus} aria-hidden>
            <Plus size={12} strokeWidth={2} color="#7AA9C7" />
          </span>
          <span className={styles.addLabel}>
            {isAddingCard ? 'Открываем оплату…' : 'Добавить новый способ оплаты'}
          </span>
        </button>
      </div>
    </Modal>
  )
}
